import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { X, Zap, FileUp, Sparkles, Loader2, FileCheck2, AlertTriangle } from "lucide-react";
import { isRecruiting, ROLE_TYPES } from "@/lib/workspace";

const CHUNK = 750 * 1024;

const formatPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const inputCls = "w-full border border-[#15233B]/15 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/40";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-1.5";

export default function QuickAddDialog({ onClose, onCreated }) {
  const recruiting = isRecruiting();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [certs, setCerts] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(false);
  const [resumeToken, setResumeToken] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [dup, setDup] = useState([]);
  const fileRef = useRef(null);
  const roleSelectOptions = position && !ROLE_TYPES.includes(position) ? [...ROLE_TYPES, position] : ROLE_TYPES;

  useEffect(() => {
    const v = name.trim();
    const phoneDigits = phone.replace(/\D/g, "");
    const hasName = v.length >= 2;
    const hasPhone = phoneDigits.length === 10;
    const hasEmail = recruiting && email.trim().length > 3;
    if (!hasName && !hasPhone && !hasEmail) { setDup([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/cards/check-duplicate", {
        params: {
          full_name: hasName ? v : undefined,
          phone: hasPhone ? phone : undefined,
          email: hasEmail ? email.trim() : undefined,
          pipeline: recruiting ? "recruiting" : "intake",
        },
      })
        .then(({ data }) => { if (!cancelled) setDup(data.matches || []); })
        .catch(() => { if (!cancelled) setDup([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [name, phone, email, recruiting]);

  const parseResume = async (file) => {
    if (!file || parsing) return;
    setErr("");
    setParsing(true);
    try {
      const { data: s } = await api.post("/resume/parse/start", { filename: file.name, total_size: file.size });
      let index = 0;
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("index", index);
        fd.append("chunk", file.slice(off, off + CHUNK));
        await api.post(`/uploads/${s.upload_id}/chunk`, fd);
        index += 1;
      }
      const { data } = await api.post(`/resume/parse/${s.upload_id}/complete`);
      setResumeToken(data.token);
      setResumeName(data.filename);
      setParsed(true);
      if (data.extracted) {
        const x = data.extracted;
        if (x.full_name) setName(x.full_name);
        if (x.phone) setPhone(formatPhone(x.phone));
        if (x.email) setEmail(x.email);
        if (x.position) setPosition(x.position);
        if (x.certifications) setCerts(x.certifications);
        if (x.city) setCity(x.city);
        toast.success("Resume read — review the details below");
      } else {
        toast.warning(data.error || "Couldn't auto-read this resume — fill the form manually");
      }
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = recruiting
        ? { full_name: name.trim(), phone, pipeline: "recruiting", email: email.trim(), position: position.trim(), certifications: certs.trim(), county: city.trim() }
        : { full_name: name.trim(), phone, payer_mco: "Unknown" };
      const { data } = await api.post("/cards", payload);
      if (recruiting && resumeToken) {
        try {
          await api.post(`/cards/${data.id}/attach-resume`, { token: resumeToken });
        } catch {
          toast.error("Candidate created, but resume attach failed — upload it again in Documents");
        }
      }
      toast.success(recruiting
        ? `${data.av_id} added — fill in position & details anytime`
        : `${data.av_id} added — fill in insurance & details anytime`);
      onCreated?.(data);
      onClose();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#15233B]/50" onClick={onClose}>
      <form
        data-testid="quick-add-dialog"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-xl shadow-2xl border border-[#15233B]/10 p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#FBBF24]" />
            <h2 className="text-lg font-extrabold text-[#15233B]">{recruiting ? "Quick add candidate" : "Quick add lead"}</h2>
          </div>
          <button type="button" data-testid="quick-add-close" onClick={onClose} className="p-1 rounded hover:bg-[#15233B]/5 text-[#15233B]/50">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-[#15233B]/55 mb-4">
          {recruiting ? "Drop a resume and AI fills the form — or just type a name." : "Just a name and phone — everything else can wait."}
        </p>
        <div className="space-y-3">
          {recruiting && (
            <div
              data-testid="resume-drop-zone"
              onClick={() => !parsing && fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); parseResume(e.dataTransfer.files?.[0]); }}
              className={`rounded-lg border-2 border-dashed px-3 py-4 text-center cursor-pointer transition-colors ${
                dragOver ? "border-[#1E73C9] bg-[#1E73C9]/5" : resumeToken ? "border-[#2E7D32]/40 bg-[#E8F5E9]/60" : "border-[#15233B]/20 hover:border-[#FBBF24] hover:bg-[#FFF9C4]/30"
              }`}
            >
              <input
                ref={fileRef}
                data-testid="resume-file-input"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
                className="hidden"
                onChange={(e) => parseResume(e.target.files?.[0])}
              />
              {parsing ? (
                <div data-testid="resume-parsing-indicator" className="flex items-center justify-center gap-2 text-xs font-bold text-[#15233B]/70">
                  <Loader2 className="h-4 w-4 animate-spin text-[#1E73C9]" /> AI is reading the resume…
                </div>
              ) : resumeToken ? (
                <div className="flex items-center justify-center gap-2 text-xs font-bold text-[#2E7D32]">
                  <FileCheck2 className="h-4 w-4" /> {resumeName} — will be attached
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-center gap-1.5 text-xs font-extrabold text-[#15233B]">
                    <FileUp className="h-4 w-4 text-[#1E73C9]" /> Upload resume
                    <Sparkles className="h-3.5 w-3.5 text-[#FBBF24]" />
                  </div>
                  <span className="text-[10px] text-[#15233B]/45 font-semibold">PDF, Word or photo — AI fills the form</span>
                </div>
              )}
            </div>
          )}
          <div>
            <label className={labelCls}>Name</label>
            <input data-testid="quick-add-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith" className={inputCls} required minLength={2} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="quick-add-phone" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(414) 555-0100" inputMode="tel" className={inputCls} />
          </div>
          {recruiting && (
            <div>
              <label className={labelCls}>Role type</label>
              <select data-testid="quick-add-role" value={position} onChange={(e) => setPosition(e.target.value)} className={inputCls}>
                <option value="">— select role —</option>
                {roleSelectOptions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          {recruiting && parsed && (
            <>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="quick-add-email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@email.com" type="email" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Certifications</label>
                  <input data-testid="quick-add-certs" value={certs} onChange={(e) => setCerts(e.target.value)}
                    placeholder="CNA, CPR" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>City</label>
                  <input data-testid="quick-add-city" value={city} onChange={(e) => setCity(e.target.value)}
                    placeholder="Milwaukee" className={inputCls} />
                </div>
              </div>
            </>
          )}
          {dup.length > 0 && (
            <div data-testid="quick-add-duplicate-warning" className="rounded-md p-2.5 bg-[#FBBF24]/10 border border-[#FBBF24]/40 text-[#7a5a1a] text-xs flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold uppercase tracking-[0.15em] text-[10px]">Possible {recruiting ? "candidate" : "lead"} match{dup.length > 1 ? `es (${dup.length})` : ""}</div>
                {dup.slice(0, 3).map((d) => (
                  <div key={d.id} className="mt-0.5">
                    {d.full_name} · <span className="font-mono">{d.av_id}</span>
                    {Array.isArray(d.match_on) && d.match_on.length > 0 && (
                      <span className="ml-1 text-[9px] font-bold uppercase tracking-wide text-[#7a5a1a]/80">{d.match_on.join(" + ")}</span>
                    )}
                  </div>
                ))}
                <div className="italic mt-1">You can still add — just checking.</div>
              </div>
            </div>
          )}
          {err && <div data-testid="quick-add-error" className="text-xs text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded px-2.5 py-2">{err}</div>}
          <button
            data-testid="quick-add-submit"
            disabled={busy || parsing || name.trim().length < 2}
            className="w-full bg-[#FFCB05] hover:bg-[#FBC02D] disabled:opacity-50 text-[#15233B] font-extrabold py-2.5 rounded-md text-sm"
          >
            {busy ? "Adding…" : recruiting ? "Add candidate" : "Add lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
